import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import levelsData from "../data/levels.json";
import topicsData from "../data/topics.json";
import { DISPLAY_YEAR_OPTIONS } from "../data/vocabYearGroups";
import "../styles/levelSelector.css";
import apiFetch from "../apiFetch";

function getUserId() {
  const stored = localStorage.getItem("userId") || localStorage.getItem("user_id");
  if (stored) return parseInt(stored, 10);
  try {
    const userJson = localStorage.getItem("user");
    if (userJson) {
      const parsed = JSON.parse(userJson);
      return parsed?.id || parsed?.userId || null;
    }
  } catch {}
  return null;
}

export default function LevelSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { link, title } = location.state || {};

  const [tier, setTier] = useState(() => {
    const saved = localStorage.getItem("difficulty_tier");
    if (saved) {
      const n = parseInt(saved, 10);
      return (n >= 0 && n <= 11) ? n : 0;
    }
    return 0;
  });
  const [topic, setTopic] = useState(topicsData.topics[0].id);

  useEffect(() => {
    // Only fetch from DB when no locally cached preference exists
    if (localStorage.getItem("difficulty_tier")) return;
    const userId = getUserId();
    if (!userId) return;
    apiFetch(`/api/user/settings/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        const savedTier = data.difficulty_tier;
        if (savedTier != null) {
          const clamped = Math.min(Math.max(parseInt(savedTier, 10), 0), 11);
          localStorage.setItem("difficulty_tier", String(clamped));
          setTier(clamped);
        }
      })
      .catch(() => {});
  }, []);

  const selectedTopicDesc = topicsData.topics.find((t) => t.id === topic)?.description || "";
  const selectedTierDesc = levelsData.tiers[String(tier)]?.description || "";

  const handleStart = () => {
    const tierName = DISPLAY_YEAR_OPTIONS.find(o => o.tier === tier)?.label || String(tier);
    navigate(link || "/games/comprehension", {
      state: { fromGameSelect: true, tier: String(tier), tierName, topicCategory: topic },
    });
  };

  return (
    <div className="level-selector">
      <div className="ls-card">
        <div className="ls-header">
          <h1 className="ls-title">{title || "Game Setup"}</h1>
          <p className="ls-subtitle">Customise your session before starting</p>
        </div>

        <div className="ls-controls">
          <div className="ls-control-group">
            <label className="ls-label">Difficulty Tier</label>
            <select
              className="ls-select"
              value={tier}
              onChange={(e) => setTier(parseInt(e.target.value, 10))}
            >
              {DISPLAY_YEAR_OPTIONS.map((opt) => (
                <option key={opt.tier} value={opt.tier}>
                  {opt.label}
                </option>
              ))}
            </select>
            {selectedTierDesc && <p className="ls-hint">{selectedTierDesc}</p>}
          </div>

          <div className="ls-control-group">
            <label className="ls-label">Topic</label>
            <select
              className="ls-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            >
              {topicsData.topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            {selectedTopicDesc && <p className="ls-hint">{selectedTopicDesc}</p>}
          </div>
        </div>

        <button className="ls-start-btn" onClick={handleStart}>
          <Play size={18} /> Start Game
        </button>

        <button className="ls-back-btn" onClick={() => navigate("/games/comprehension")}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>
    </div>
  );
}
